import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MetabaseEmbedComponent } from '../../../components/metabase-embed/metabase-embed.component';
import { RoutingTabComponent } from '../../../components/ui/tabs/routing-tab/routing-tab.component';
import { UserProfileService } from '../../../services/user/user-profile.service';
import { DeliveryReportService, PeriodOutcome } from '../../../services/reports/delivery-report.service';
import { deliveredShare, formatShare } from '../../../services/message/delivery.service';
import {
  DEFAULT_REPORT_PERIOD,
  ReportPeriodDays,
  periodPhrase,
} from '../../../services/reports/report-period';
import { formatCount } from '../../../../utils/sms';
import { environment } from '../../../../environments/environment';
import { ReportPeriodComponent } from './report-period/report-period.component';
import { FailuresReportComponent } from './failures-report/failures-report.component';
import { SentReportComponent } from './sent-report/sent-report.component';

export type ReportsTab = 'overview' | 'failures' | 'sent';

interface RoutingTab {
  title: string;
  count?: number;
  link: string;
  queryParams: Record<string, string>;
}

const PAGE_LINK = '/organization/reports';

/**
 * Delivery reports (/organization/reports): how the organisation's messages
 * landed in a chosen period. A figure strip from delivery receipts, then
 * three views: the Metabase dashboard, the recipients whose SMS failed, and
 * the sends with their SMS parts.
 */
@Component({
  selector: 'app-messaging-reports',
  standalone: true,
  imports: [
    CommonModule,
    MatTooltipModule,
    MetabaseEmbedComponent,
    RoutingTabComponent,
    ReportPeriodComponent,
    FailuresReportComponent,
    SentReportComponent,
  ],
  templateUrl: './messaging-reports.component.html',
  styleUrl: './messaging-reports.component.css',
})
export class MessagingReportsComponent implements OnInit {
  private userService = inject(UserProfileService);
  private reports = inject(DeliveryReportService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  /**
   * The SMS dashboard (#6 on the main Metabase), the same one API traffic
   * shows: its figures match the portal's own. The delivery dashboard on the
   * second Metabase (environment.metabaseMessaging, #3) read the wrong data.
   */
  readonly dashboardId = environment.metabase.smsDashboardId;
  readonly orgId = this.userService.jwtOrgId;
  readonly formatCount = formatCount;

  @ViewChild(MetabaseEmbedComponent) embed?: MetabaseEmbedComponent;

  /** Full screen needs the browser's permission and a live dashboard. */
  readonly canFullscreen =
    typeof document !== 'undefined' &&
    !!document.fullscreenEnabled &&
    !(environment as { devMockApi?: boolean }).devMockApi;

  tab: ReportsTab = 'overview';
  days: ReportPeriodDays = DEFAULT_REPORT_PERIOD;
  /** Bumped by Refresh so the active table reloads. */
  reloadKey = 0;

  outcome: PeriodOutcome | null = null;
  outcomeState: 'loading' | 'ready' | 'error' = 'loading';
  tabs: RoutingTab[] = this.buildTabs();

  private outcome$ = new Subject<void>();

  get periodText(): string {
    return periodPhrase(this.days);
  }

  get description(): string {
    switch (this.tab) {
      case 'failures':
        return `Recipients whose SMS did not arrive in ${this.periodText}, newest first.`;
      case 'sent':
        return `Messages created in ${this.periodText} that have gone out: how each landed and the SMS parts it used.`;
      default:
        return `How your messages landed in ${this.periodText}, counted from delivery receipts.`;
    }
  }

  /** Failed recipients in the period, 0 until the counts load. */
  get failedCount(): number {
    return this.outcome ? this.outcome.current.failed : 0;
  }

  get rate(): string {
    const current = this.outcome?.current;
    return current && current.total ? formatShare(deliveredShare(current)) : '—';
  }

  /** "Up 2.4 points on the previous 30 days", or null without a comparison. */
  get rateChange(): { text: string; tone: 'ok' | 'bad' | 'same' } | null {
    const current = this.outcome?.current;
    const previous = this.outcome?.previous;
    if (!current?.total || !previous?.total) return null;
    const points = (deliveredShare(current) - deliveredShare(previous)) * 100;
    const rounded = Math.round(Math.abs(points) * 10) / 10;
    const span = `the previous ${this.days} days`;
    if (rounded < 0.1) return { text: `Same as ${span}`, tone: 'same' };
    const unit = rounded === 1 ? 'point' : 'points';
    return points > 0
      ? { text: `Up ${rounded} ${unit} on ${span}`, tone: 'ok' }
      : { text: `Down ${rounded} ${unit} on ${span}`, tone: 'bad' };
  }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const tab = params.get('tab');
      this.tab = tab === 'failures' || tab === 'sent' ? tab : 'overview';
    });

    this.outcome$
      .pipe(
        switchMap(() => {
          this.outcomeState = 'loading';
          return this.reports.periodOutcome(this.days).pipe(
            map((outcome) => ({ outcome, failed: false })),
            catchError(() => of({ outcome: null, failed: true }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ outcome, failed }) => {
        this.outcome = outcome;
        this.outcomeState = failed ? 'error' : 'ready';
        this.tabs = this.buildTabs();
      });

    this.outcome$.next();
  }

  setDays(days: ReportPeriodDays): void {
    if (days === this.days) return;
    this.days = days;
    this.outcome = null;
    this.tabs = this.buildTabs();
    this.outcome$.next();
  }

  refresh(): void {
    this.reloadKey += 1;
    this.outcome$.next();
    this.embed?.refresh();
  }

  reloadDashboard(): void {
    this.embed?.refresh();
  }

  openFullscreen(): void {
    this.embed?.enterFullscreen();
  }

  showFailures(): void {
    this.router.navigate([PAGE_LINK], { queryParams: { tab: 'failures' } });
  }

  private buildTabs(): RoutingTab[] {
    const failed = this.outcome?.current.failed;
    return [
      { title: 'Overview', link: PAGE_LINK, queryParams: {} },
      { title: 'Failures', link: PAGE_LINK, queryParams: { tab: 'failures' }, ...(failed !== undefined ? { count: failed } : {}) },
      { title: 'Sent messages', link: PAGE_LINK, queryParams: { tab: 'sent' } },
    ];
  }
}
